import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  IconButton,
  Image,
  Divider,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Flex,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  Plus,
  Minus,
  Trash2,
  ShoppingBag,
  CreditCard,
  MapPin,
} from 'lucide-react';
import { 
  TableCartItem, 
  TableInfo, 
  TableOrderingContext,
  GuestInfo 
} from '../../types/tableOrdering';
import GuestInfoModal from './GuestInfoModal';
import { useHotelCurrencyFromTable } from '../../hooks/useHotelCurrency';
import { tableOrderingService } from '../../services/tableOrderingService';
import { saveOrder } from '../../utils/orderStorage';

interface TableCartProps {
  cartItems: TableCartItem[];
  tableInfo: TableInfo;
  context: TableOrderingContext;
  onUpdateQuantity: (menuItemId: string, newQuantity: number, specialInstructions?: string) => void;
  onRemoveItem: (menuItemId: string, specialInstructions?: string) => void;
  onCartClose: () => void;
  onOrderSuccess?: (orderData: any, guestInfo: any) => void;
}

/**
 * TableCart Component
 * Displays cart items and handles checkout flow for table ordering
 */
const TableCart: React.FC<TableCartProps> = ({
  cartItems,
  tableInfo,
  context,
  onUpdateQuantity,
  onRemoveItem,
  onCartClose,
  onOrderSuccess
}) => {
  const { t } = useTranslation();
  const [isGuestInfoOpen, setIsGuestInfoOpen] = useState(false);
  const [orderType, setOrderType] = useState<'table_service' | 'pickup'>('table_service');

  // Use hotel currency hook for consistent formatting
  const { formatAmount: formatHotelAmount } = useHotelCurrencyFromTable(tableInfo);
  
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Subtotal only — the kitchen bill applies the hotel's real tax rates,
  // so an invented client-side rate would mismatch what the guest pays.
  const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal;

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast({
        title: t('tableOrdering.cartView.emptyCartTitle'),
        description: t('tableOrdering.cartView.emptyCartDesc'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsGuestInfoOpen(true);
  };

  const handleGuestInfoSubmit = async (guestInfo: GuestInfo) => {
    try {
      // Prepare order data to match backend structure
      const orderData = {
        restaurant: parseInt(context.restaurantId),
        table: parseInt(context.tableId),
        type: orderType === 'table_service' ? 'dine_in' : 'pickup',
        customer_name: guestInfo.name,
        customer_phone: guestInfo.phone,
        customer_email: guestInfo.email || null,
        delivery_instructions: guestInfo.special_requests || '',
        notes: '',
        kitchen_notes: '',
        items: cartItems.map(item => ({
          menu_item: parseInt(item.menu_item_id),
          quantity: item.quantity,
          special_instructions: item.special_instructions || ''
        }))
      };
      // Place the order
      const response = await tableOrderingService.placeTableOrder(orderData);
      
      if (response.success && response.data) {
        setIsGuestInfoOpen(false);
        onCartClose();
        
        // Save order to localStorage with all necessary data
        const savedOrder = saveOrder(response.data, guestInfo, context);
        // Clear the cart items and navigate to tracking
        if (onOrderSuccess) {
          onOrderSuccess(response.data, guestInfo);
        }
        
        toast({
          title: t('tableOrdering.cartView.orderPlaced'),
          description: t('tableOrdering.cartView.orderPlacedDesc', { number: response.data.order_number }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
      } else {
        throw new Error('Failed to place order');
      }
      
    } catch (error: any) {
      toast({
        title: t('tableOrdering.cartView.orderFailed'),
        description: error.message || t('tableOrdering.cartView.orderFailedDefault'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (cartItems.length === 0) {
    return (
      <Box p={6} textAlign="center">
        <VStack spacing={4}>
          <Icon as={ShoppingBag} boxSize={12} color="gray.400" />
          <Text fontSize="lg" color="gray.600">
            {t('tableOrdering.cartView.empty')}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {t('tableOrdering.cartView.emptyHint')}
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={4} align="stretch" px={6}>
        
        {/* Order Type Selection */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
            {t('tableOrdering.cartView.orderType')}
          </Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant={orderType === 'table_service' ? 'solid' : 'outline'}
              colorScheme={orderType === 'table_service' ? 'blue' : 'gray'}
              onClick={() => setOrderType('table_service')}
              leftIcon={<Icon as={MapPin} />}
              flex={1}
            >
              {t('tableOrdering.cartView.tableService')}
            </Button>
            <Button
              size="sm"
              variant={orderType === 'pickup' ? 'solid' : 'outline'}
              colorScheme={orderType === 'pickup' ? 'blue' : 'gray'}
              onClick={() => setOrderType('pickup')}
              leftIcon={<Icon as={ShoppingBag} />}
              flex={1}
            >
              {t('tableOrdering.cartView.pickup')}
            </Button>
          </HStack>
          
          <Text fontSize="xs" color="gray.500" mt={2}>
            {orderType === 'table_service'
              ? t('tableOrdering.cartView.tableServiceHint')
              : t('tableOrdering.cartView.pickupHint')
            }
          </Text>
        </Box>

        {/* Cart Items */}
        <VStack spacing={3} align="stretch">
          {cartItems.map((item, index) => (
            <Card key={`${item.menu_item_id}-${index}`} variant="outline" bg={cardBg}>
              <CardBody p={4}>
                <Flex gap={3} align="start">
                  {item.image_url && (
                    <Image
                      src={item.image_url}
                      alt={item.name}
                      boxSize="50px"
                      objectFit="cover"
                      borderRadius="md"
                      flexShrink={0}
                    />
                  )}
                  
                  <VStack align="start" flex="1" spacing={1}>
                    <Text fontWeight="semibold" fontSize="sm">
                      {item.name}
                    </Text>
                    
                    {item.special_instructions && (
                      <Text fontSize="xs" color="gray.600" fontStyle="italic">
                        {t('tableOrdering.cartView.noteLabel', { instructions: item.special_instructions })}
                      </Text>
                    )}
                    
                    <HStack justify="space-between" w="full">
                      <Text fontSize="sm" fontWeight="bold" color="blue.600">
                        {formatHotelAmount(item.price)}
                      </Text>
                      
                      <HStack spacing={1}>
                        <IconButton
                          size="xs"
                          variant="outline"
                          aria-label={t('tableOrdering.cartView.decreaseQty')}
                          icon={<Icon as={Minus} />}
                          onClick={() => onUpdateQuantity(item.menu_item_id, item.quantity - 1, item.special_instructions)}
                        />
                        <Text fontSize="sm" fontWeight="semibold" minW="25px" textAlign="center">
                          {item.quantity}
                        </Text>
                        <IconButton
                          size="xs"
                          variant="outline"
                          aria-label={t('tableOrdering.cartView.increaseQty')}
                          icon={<Icon as={Plus} />}
                          onClick={() => onUpdateQuantity(item.menu_item_id, item.quantity + 1, item.special_instructions)}
                        />
                        <IconButton
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          aria-label={t('tableOrdering.cartView.removeItem')}
                          icon={<Icon as={Trash2} />}
                          onClick={() => onRemoveItem(item.menu_item_id, item.special_instructions)}
                          ml={2}
                        />
                      </HStack>
                    </HStack>
                    
                    <Text fontSize="sm" fontWeight="bold" color="gray.800">
                      {t('tableOrdering.cartView.totalLabel', { amount: formatHotelAmount(item.total) })}
                    </Text>
                  </VStack>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </VStack>

        <Divider />

        {/* Order Summary */}
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between">
            <Text fontSize="sm">{t('tableOrdering.cartView.subtotal')}</Text>
            <Text fontSize="sm" fontWeight="semibold">
              {formatHotelAmount(subtotal)}
            </Text>
          </HStack>

          <Divider />

          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="bold">{t('tableOrdering.cartView.total')}</Text>
            <Text fontSize="lg" fontWeight="bold" color="blue.600">
              {formatHotelAmount(total)}
            </Text>
          </HStack>
          <Text fontSize="xs" color="gray.500">
            {t('tableOrdering.cartView.taxNote')}
          </Text>
        </VStack>

        {/* Table Info */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box fontSize="sm">
            <Text fontWeight="semibold">
              {t('tableOrdering.cartView.tableHeader', { restaurant: tableInfo.restaurant.name, table: tableInfo.table_number })}
            </Text>
            <Text color="gray.600">
              {orderType === 'table_service'
                ? t('tableOrdering.cartView.tableServiceDeliver')
                : t('tableOrdering.cartView.pickupDeliver')
              }
            </Text>
          </Box>
        </Alert>

        {/* Checkout Button */}
        <Button
          colorScheme="blue"
          size="lg"
          leftIcon={<Icon as={CreditCard} />}
          onClick={handleCheckout}
          isDisabled={cartItems.length === 0}
        >
          {t('tableOrdering.cartView.checkout')}
        </Button>
      </VStack>

      {/* Guest Info Modal */}
      <GuestInfoModal
        isOpen={isGuestInfoOpen}
        onClose={() => setIsGuestInfoOpen(false)}
        onSubmit={handleGuestInfoSubmit}
        tableInfo={tableInfo}
        cartItems={cartItems}
        orderType={orderType}
        total={total}
      />
    </Box>
  );
};

export default TableCart;